// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"

// Utils
import { ClientUtility as Client } from '../utils/client-utility'

/**
 * Image upload parameters
 * @typedef UploadForm
 * @type {Object}
 * @property {File} image - Image File
 */

/**
 * Image upload result properties
 * @typedef UploadResult
 * @type {Object}
 * @property {String} uri   - Uploaded URI
 * @property {String} name  - Uploaded name
 */

/**
 * Image deletion result properties
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Remove state
 * @property {String} filename - Remove filename
 */

export class NonScProjectImageClient {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/project/nonsc/image`
  )

  static isURL(input) {
    return /^(http|https)(:\/{2})([^\W+]\S+)/gm.test(input)
  }

  static type = {
    icon  : 'nonsc-icon',
    cover : 'nonsc-cover',
  }

  /**
   * Upload Project Image
   * @param {String}      type    - Image Type
   * @param {UploadForm}  payload - Upload Payload
   * @returns {Promise<UploadResult>} Result
   */
  static async upload(type, payload) {

    // Request URL
    const url = `${this.endpoint}/${type}`

    // Default Result
    let result = {
      name: null, uri: null
    }

    try {

      // Image Type Validator
      const allowed = (
        Object.values(this.type).includes(type)
      )

      // Validate Image Type
      if (!allowed) {
        throw new Error('Invalid Image Type')
      }

      // Request Headers
      const headers = accessHeaders()
      
      // Request Project List
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = data.result
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'project-nonsc-image.upload', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Remove Project Image
   * @param {String} type - Image Type
   * @param {String} name - Filename or URI
   * @returns {Promise<DeleteResult>} Result
   */
  static async remove(type, name) {

    // Format Filename
    const filename = (
      this.isURL(name) ? name.split('/').pop() : name
    )

    // Request URL
    const url = (
      `${this.endpoint}/${type}/${filename}`
    )

    // Default Result
    let result = {
      filename: null, deleted: false
    }

    try {

      // Request Headers
      const headers = accessHeaders()
      
      // Request Project List
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = data.result
      }
    }
    catch (error) {
     
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'project-nonsc-image.remove', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }
}